import {reportTraceData} from '@github-ui/api-insights-tracing'
import {create, fromJson} from '@github-ui/dotcom-schema/protobuf'
import {SafeHtmlSchema} from '@github-ui/dotcom-schema/types/common/v1/safe_html'
import {getAppTypeHeader} from '@github-ui/fetch-headers'
import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {QueryRouteQueryType} from '@github-ui/react-core/future/data-router-types'
import {
  type JsonResponse,
  type MainQueryResult,
  makeSelectDataFromQueryData,
} from '@github-ui/react-core/future/main-query'
import {railsPartialQuery} from '@github-ui/react-core/future/rails-partial-query'
import {fetchWithSafeHTMLNonceAndProtoSignal} from '@github-ui/react-core/future/query-fn-fetch'
import {repoAppBuilder} from '@github-ui/repo-app-builder'
import type {ReleasesIndexData, TagsIndexData} from '../types'

function indexQueryDeps({pathname, searchParams}: {pathname: string; searchParams: URLSearchParams}) {
  return {pathname, searchParams: searchParams.toString()}
}

function createIndexResponseFetcher() {
  const pending = new Map<string, ReturnType<typeof fetchWithSafeHTMLNonceAndProtoSignal>>()

  return async <Res>(queryKey: Parameters<typeof fetchWithSafeHTMLNonceAndProtoSignal>[0]) => {
    const key = JSON.stringify([queryKey.queryDeps.pathname, queryKey.queryDeps.searchParams])
    let response = pending.get(key)
    if (!response) {
      // Share only in-flight responses so later navigations and invalidations still fetch fresh data.
      response = fetchWithSafeHTMLNonceAndProtoSignal({
        ...queryKey,
        queryDeps: {
          ...queryKey.queryDeps,
          init: {
            ...queryKey.queryDeps.init,
            headers: {...getAppTypeHeader('dataRouter'), ...queryKey.queryDeps.init?.headers},
          },
        },
      }).finally(() => pending.delete(key))
      pending.set(key, response)
    }
    const result = await response
    reportTraceData(result.json)
    return {...result, json: result.json as Res}
  }
}

function indexMainQuery<Res>(fetchResponse: ReturnType<typeof createIndexResponseFetcher>) {
  return {
    queryName: 'mainQuery' as const,
    queryDeps: indexQueryDeps,
    type: QueryRouteQueryType.Blocking,
    // Refresh metadata alongside the network-first partials, including on cached-page navigation.
    navigationBehavior: 'network-first' as const,
    select: makeSelectDataFromQueryData<Res>(),
    queryFn: async (
      queryKey: Parameters<typeof fetchWithSafeHTMLNonceAndProtoSignal>[0] & {routeId: string},
    ): Promise<MainQueryResult<Res>> => {
      const {json} = await fetchResponse<JsonResponse<Res>>(queryKey)
      const payload = json.payload?.[queryKey.routeId]
      if (!payload) {
        throw new Error(`Unable to find payload for route Id: ${queryKey.routeId}`)
      }
      return {meta: json.meta, payload}
    },
  }
}

function indexPartialQuery(partialName: string, fetchResponse: ReturnType<typeof createIndexResponseFetcher>) {
  return (routeId: string) => {
    const config = railsPartialQuery({partialName, queryDeps: indexQueryDeps})(routeId)
    return {
      ...config,
      queryFn: async (queryKey: Parameters<typeof config.queryFn>[0]) => {
        const {json, isProtoJson} = await fetchResponse<{payload: Record<string, unknown>}>(queryKey)
        const html = json.payload?.[config.queryName]
        if (html === undefined || html === null) {
          throw new Error(`Unable to find payload for query: ${config.queryName}`)
        }
        if (isProtoJson) {
          return fromJson({schema: SafeHtmlSchema, raw: html, routeId, queryName: config.queryName})
        }
        if (typeof html !== 'string') {
          throw new Error(`Expected HTML string for query: ${config.queryName}`)
        }
        return create(SafeHtmlSchema, {value: html})
      },
    }
  }
}

export function buildReleasesIndexRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  const fetchResponse = createIndexResponseFetcher()
  return appBuilder.createQueryRouteConfig('releasesIndexRoute', {
    path: '/:owner/:repo/releases',
    queries: [
      indexMainQuery<ReleasesIndexData>(fetchResponse),
      indexPartialQuery('Filter', fetchResponse),
      indexPartialQuery('Results', fetchResponse),
    ],
  })
}

export function buildTagsIndexRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  const fetchResponse = createIndexResponseFetcher()
  return appBuilder.createQueryRouteConfig('tagsIndexRoute', {
    path: '/:owner/:repo/tags',
    queries: [indexMainQuery<TagsIndexData>(fetchResponse), indexPartialQuery('Results', fetchResponse)],
  })
}

export const releasesIndexRoute = buildReleasesIndexRoute(repoAppBuilder)
export const tagsIndexRoute = buildTagsIndexRoute(repoAppBuilder)
